import { useEffect, useState } from "react";
import { Smartphone, Download, X, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const DISMISS_KEY = "leadflow_pwa_dismissed_at";
const DISMISS_DAYS = 7; // re-nudge after a week

// iOS Safari doesn't fire `beforeinstallprompt` — detect it separately.
const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const iPadOS = ua.includes("Mac") && "ontouchend" in document; // iPad in desktop mode
  return iOSDevice || iPadOS;
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true // iOS
  );
};

const wasRecentlyDismissed = () => {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  if (!ts) return false;
  return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
};

export default function InstallPrompt() {
  const [prompt, setPrompt] = useState(null); // BeforeInstallPromptEvent
  const [showIOS, setShowIOS] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    const onBip = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", () => {
      setPrompt(null);
      setShowIOS(false);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    });

    // Fallback for iOS Safari
    if (isIOS()) setShowIOS(true);

    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  if (dismissed) return null;
  if (!prompt && !showIOS) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const install = async () => {
    // Snapshot the event locally — state may be nulled out mid-flow.
    const bip = prompt;
    if (!bip || typeof bip.prompt !== "function") {
      toast.info("Install isn't available in this browser. Try Chrome/Edge on Android or use the browser menu → Install app.");
      setPrompt(null);
      return;
    }
    setInstalling(true);
    try {
      // Some browsers require prompt() to be triggered inside the same tick as the user gesture.
      const p = bip.prompt();
      if (p && typeof p.then === "function") await p;
      let outcome = "dismissed";
      try {
        const choice = await bip.userChoice;
        outcome = choice?.outcome || "dismissed";
      } catch (e) {
        // Some UAs throw on userChoice access after prompt() rejects — ignore.
        // eslint-disable-next-line no-console
        console.debug("[PWA] userChoice error", e);
      }
      if (outcome === "accepted") {
        toast.success("Installed — launch LeadFlow from your home screen anytime.");
      }
      // Whatever the outcome, snooze the banner so we don't spam.
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
      setDismissed(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[PWA] install prompt failed", err);
      toast.error("Your browser blocked the install prompt. Look for the '+ Install' icon in the address bar, or use the browser menu → Install app.");
    } finally {
      setInstalling(false);
      setPrompt(null);
    }
  };

  return (
    <div
      className="relative overflow-hidden rounded-xl border border-[#1D63ED]/25 bg-gradient-to-r from-[#EEF3FE] via-white to-[#EEF3FE] p-4 md:p-5 flex items-center gap-4"
      data-testid="pwa-install-banner"
    >
      <div className="w-11 h-11 rounded-lg bg-white border border-[#1D63ED]/20 flex items-center justify-center flex-shrink-0 shadow-sm">
        <Smartphone className="w-5 h-5 text-[#1D63ED]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">Add LeadFlow to your home screen</p>
        {prompt ? (
          <p className="text-xs text-slate-600 mt-0.5">
            Open your leads in 1 tap from anywhere — no browser tabs, no bookmarks. Works offline for the shell.
          </p>
        ) : (
          <p className="text-xs text-slate-600 mt-0.5 flex items-center flex-wrap gap-x-1">
            Tap
            <Share className="w-3.5 h-3.5 inline-block text-[#1D63ED]" />
            <span className="font-medium">Share</span>
            in Safari, then choose
            <span className="font-medium">"Add to Home Screen"</span> — LeadFlow opens like a native app.
          </p>
        )}
      </div>
      {prompt && (
        <Button
          onClick={install}
          disabled={installing}
          className="bg-[#1D63ED] hover:bg-[#1854D9] text-white flex-shrink-0"
          data-testid="pwa-install-btn"
        >
          <Download className="w-4 h-4 mr-1.5" />
          {installing ? "Installing…" : "Install app"}
        </Button>
      )}
      <button
        onClick={dismiss}
        className="w-8 h-8 rounded-md hover:bg-white flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors flex-shrink-0"
        aria-label="Dismiss install prompt"
        data-testid="pwa-install-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
